import { useState, useCallback, useRef } from "react";
import { z } from "zod";
import {
  ErrorKind,
  FieldMeta,
  FieldMetaMap,
  FieldState,
  FormApi,
  ResolvedFieldProps,
  SchemaConfig,
  SelectOption,
  ValidationResult,
  WidgetType,
} from "./types";
import {
  getActiveSchema,
  getActiveValidator,
  getSchemaFieldNames,
  isFieldRequired as schemaIsFieldRequired,
  stripFieldsNotInSchema,
  inferWidgetType,
  validateSchema,
} from "./schema-utils";

/**
 * useFormV2 – form hook driven by Zod schemas.
 *
 * The Zod schema is the source of truth for:
 *   - Which fields exist (visibility)
 *   - Which fields are required
 *   - Validation + per-field error messages
 *   - Auto-clearing fields when the discriminator changes
 *
 * FieldMetaMap provides UI-only concerns: labels, widget types, tooltips,
 * options, derived values, side-effects.
 */
export function useFormV2<TForm extends Record<string, unknown>>(
  schemaConfig: SchemaConfig,
  fieldMeta: FieldMetaMap<TForm>,
  initialValues: TForm,
  opts?: {
    /**
     * Extra fields that the form manages but that are NOT in
     * any Zod schema (e.g. adj_*_two, adj_*_three).  These are always
     * visible/optional and never auto-cleared.
     */
    extraFields?: string[];
    /**
     * Additional validation beyond Zod.  Runs after schema validation.
     * Return a map of field name → error message for any extra errors.
     * These are merged with Zod errors and participate in isValid / errorKind.
     */
    customValidation?: (values: TForm) => Record<string, string>;
  },
): FormApi<TForm> {
  const initialRef = useRef(initialValues);

  const [values, setValuesRaw] = useState<TForm>(() => ({ ...initialValues }));
  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>(
    () => buildInitialFieldStates(fieldMeta as FieldMetaMap<Record<string, unknown>>),
  );
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const extraFieldSet = new Set(opts?.extraFields || []);

  // ---- Active schema (recomputed every render) ----
  const discriminatorValue = values[schemaConfig.discriminatorField] as
    | string
    | undefined;
  const activeSchema = getActiveSchema(schemaConfig, discriminatorValue);
  const activeFieldNames = getSchemaFieldNames(activeSchema);

  // ---- Apply derived fields ----
  const applyDerived = (vals: TForm): TForm => {
    let next = { ...vals };
    for (const [key, meta] of Object.entries(fieldMeta)) {
      if (meta.derive) {
        (next as Record<string, unknown>)[key] = meta.derive(next);
      }
    }
    return next;
  };

  const derivedValues = applyDerived(values);

  // ---- Validation ----
  const fieldMetaUntyped = fieldMeta as FieldMetaMap<Record<string, unknown>>;
  const activeValidator = getActiveValidator(schemaConfig, discriminatorValue);

  const fieldErrors = (() => {
    const result = validateSchema(activeValidator, derivedValues as Record<string, unknown>, fieldMetaUntyped);
    const zodErrors = result.errors || {};
    const customErrors = opts?.customValidation ? opts.customValidation(derivedValues) : {};
    return { ...zodErrors, ...customErrors };
  })();

  const isValid = Object.keys(fieldErrors).length === 0;

  // ---- Schema introspection ----
  const isFieldInSchema = (name: string): boolean => {
    return activeFieldNames.has(name) || extraFieldSet.has(name);
  };

  const isFieldRequiredFn = (name: string): boolean => {
    return schemaIsFieldRequired(activeSchema, name);
  };

  // ---- setFieldValue ----
  const setFieldValue = useCallback(
    (name: string, value: unknown) => {
      setValuesRaw((prev) => {
        let next = { ...prev, [name]: value } as TForm;

        // Run effects
        const meta = fieldMeta[name];
        if (meta?.effects) {
          const patch = meta.effects(value, next, prev);
          if (patch) {
            next = { ...next, ...patch };
          }
        }

        // If this is the discriminator field, strip fields not in the new schema
        if (name === schemaConfig.discriminatorField) {
          const newSchema = getActiveSchema(
            schemaConfig,
            value as string | undefined,
          );
          next = stripFieldsNotInSchema(next, newSchema, schemaConfig.base);
          // Re-apply the discriminator itself (strip won't remove base fields)
          (next as Record<string, unknown>)[name] = value;
        }

        return next;
      });

      setFieldStates((prev) => ({
        ...prev,
        [name]: { ...prev[name], touched: true },
      }));
    },
    [fieldMeta, schemaConfig],
  );

  const setFieldValues = useCallback((patch: Partial<TForm>) => {
    setValuesRaw((prev) => ({ ...prev, ...patch }));
  }, []);

  const touchField = useCallback((name: string) => {
    setFieldStates((prev) => ({
      ...prev,
      [name]: { ...prev[name], touched: true },
    }));
  }, []);

  const attemptSubmit = useCallback(() => {
    setHasAttemptedSubmit(true);
    setFieldStates((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        next[key] = { ...next[key], touched: true };
      }
      return next;
    });
  }, []);

  const validate = useCallback((): ValidationResult<TForm> => {
    const result = validateSchema(activeValidator, derivedValues as Record<string, unknown>, fieldMetaUntyped);
    const customErrors = opts?.customValidation ? opts.customValidation(derivedValues) : {};
    const allErrors = { ...(result.errors || {}), ...customErrors };
    if (Object.keys(allErrors).length > 0) {
      return { success: false, errors: allErrors };
    }
    return result as ValidationResult<TForm>;
  }, [activeValidator, derivedValues, fieldMetaUntyped, opts]);

  const reset = useCallback(
    (nextInitial?: Partial<TForm>) => {
      const base = nextInitial
        ? { ...initialRef.current, ...nextInitial }
        : { ...initialRef.current };
      if (nextInitial) initialRef.current = base;
      setValuesRaw(base);
      setFieldStates(buildInitialFieldStates(fieldMeta as FieldMetaMap<Record<string, unknown>>));
      setHasAttemptedSubmit(false);
    },
    [fieldMeta],
  );

  // ---- getFieldProps ----
  const getFieldProps = (name: string): ResolvedFieldProps | null => {
    const meta = fieldMeta[name];
    if (!meta) return null;

    const vals = derivedValues;
    const inSchema = isFieldInSchema(name);

    // Visibility: in schema (or extra field), and optionally further restricted by meta.visible
    let visible = inSchema;
    if (visible && meta.visible) {
      visible = meta.visible(vals);
    }

    // requiredOverride takes priority over static Zod schema introspection.
    // See FieldMeta.requiredOverride for why this exists.
    const required = (() => {
      if (meta.requiredOverride != null) {
        return typeof meta.requiredOverride === "function"
          ? meta.requiredOverride(vals)
          : meta.requiredOverride;
      }
      return isFieldRequiredFn(name);
    })();

    const disabled = (() => {
      if (meta.derive) return true; // derived = always disabled
      if (typeof meta.disabled === "function") return meta.disabled(vals);
      return meta.disabled ?? false;
    })();

    // Widget type: use meta override, or infer from schema
    const widget: WidgetType = (() => {
      if (meta.widget) return meta.widget;
      const shape = activeSchema.shape;
      if (shape[name]) return inferWidgetType(shape[name]);
      return "text";
    })();

    const options: SelectOption[] =
      typeof meta.options === "function" ? meta.options(vals) : meta.options ?? [];

    const error = fieldErrors[name];
    const errorKind: ErrorKind | undefined = error ? classifyError(error) : undefined;
    const touched = fieldStates[name]?.touched ?? false;
    // "format" errors (cross-field validation like "end date must be after start
    // date") should show as soon as the field has a value — the user doesn't need
    // to have directly touched this specific field for the error to be relevant.
    // "required" errors still require the field to be touched or submit attempted.
    const showError = !!error && (
      hasAttemptedSubmit
      || touched
      || (errorKind === "format" && (vals as Record<string, unknown>)[name] != null)
    );

    return {
      name,
      label: meta.label,
      widget,
      required,
      disabled,
      visible,
      placeholder: meta.placeholder,
      tooltip: meta.tooltip,
      className: meta.className,
      step: meta.step,
      min: meta.min,
      max: meta.max,
      options,
      value: (vals as Record<string, unknown>)[name],
      error,
      errorKind,
      showError,
    };
  };

  return {
    values: derivedValues,
    fieldStates,
    hasAttemptedSubmit,
    setFieldValue,
    setFieldValues,
    touchField,
    attemptSubmit,
    validate,
    reset,
    isValid,
    activeSchema,
    activeFieldNames,
    isFieldRequired: isFieldRequiredFn,
    isFieldInSchema,
    getFieldProps,
  };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function buildInitialFieldStates(
  meta: FieldMetaMap<Record<string, unknown>>,
): Record<string, FieldState> {
  const out: Record<string, FieldState> = {};
  for (const key of Object.keys(meta)) {
    out[key] = { touched: false };
  }
  return out;
}

/**
 * Classify a Zod error message as either "required" (field is empty/unselected)
 * or "format" (value is present but malformed).
 *
 * "required" errors only get a red border + red Required label — no message text.
 * "format" errors show the actual message below the input.
 */
const REQUIRED_ERROR_PATTERNS = [
  /^required$/i,
  /^invalid uuid$/i,
  /must contain at least 1 character/i,
  /expected string, received (null|undefined)/i,
  /expected number, received (null|undefined|nan)/i,
  /expected date, received (null|undefined)/i,
];

function classifyError(error: string): ErrorKind {
  for (const pattern of REQUIRED_ERROR_PATTERNS) {
    if (pattern.test(error)) return "required";
  }
  return "format";
}
